import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle, AlertTriangle, Calendar, Package, DollarSign, Edit, X, Save, Truck, Search, RefreshCw } from 'lucide-react';
import { colors } from '../theme';

const Validacao = ({ user }) => {
  const [pedidos, setPedidos] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [transportadoras, setTransportadoras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [editando, setEditando] = useState(null);
  const [formEdit, setFormEdit] = useState({});
  const [salvando, setSalvando] = useState(false);
  
  // Filtros e paginação
  const [busca, setBusca] = useState('');
  const [buscaInput, setBuscaInput] = useState(''); // Input separado para debounce
  const [filtroTipo, setFiltroTipo] = useState('TODOS');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [itensPorPagina] = useState(50);

  // Carregar dados com paginação do backend
  const loadData = useCallback(async (page = 1, search = '', filtro = '') => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('primaza_token');
      
      // Construir query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itensPorPagina.toString()
      });
      
      if (search) params.append('busca', search);
      if (filtro && filtro !== 'TODOS') params.append('filtro_campo', filtro);
      
      const [pedidosRes, statsRes] = await Promise.all([
        axios.get(`${API}/pedidos/validacao/incompletos?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }),
        axios.get(`${API}/pedidos/validacao/estatisticas`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        })
      ]);
      
      // Agora o backend retorna um objeto com paginação
      const { pedidos: pedidosData, total, total_pages } = pedidosRes.data;
      
      setPedidos(pedidosData);
      setTotalPedidos(total);
      setTotalPaginas(total_pages);
      setEstatisticas(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados de validação:', error);
      const msg = error.response?.status === 403
        ? 'Acesso negado. Verifique suas permissões.'
        : error.response?.status === 401
        ? 'Sessão expirada. Faça login novamente.'
        : error.code === 'ECONNABORTED' || error.message?.includes('timeout')
        ? 'Tempo limite excedido. O servidor demorou a responder.'
        : `Erro ao carregar dados: ${error.response?.data?.detail || error.message}`;
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, [itensPorPagina]);

  const loadTransportadoras = async () => {
    try {
      const response = await axios.get(`${API}/transportadoras`);
      const sorted = response.data.sort((a, b) => a.nome.localeCompare(b.nome));
      setTransportadoras(sorted);
    } catch (error) {
      console.error('Erro ao carregar transportadoras:', error);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    loadData(1, '', '');
    loadTransportadoras();
  }, [loadData]);

  // Debounce para busca - 1 segundo para evitar travamento
  useEffect(() => {
    const timer = setTimeout(() => {
      if (buscaInput !== busca) {
        setBusca(buscaInput);
        setPaginaAtual(1);
        loadData(1, buscaInput, filtroTipo);
      }
    }, 1000); // 1 segundo de debounce
    
    return () => clearTimeout(timer);
  }, [buscaInput]);

  // Quando filtro muda
  const handleFiltroChange = (novoFiltro) => {
    setFiltroTipo(novoFiltro);
    setPaginaAtual(1);
    loadData(1, busca, novoFiltro);
  };

  // Navegação de página
  const irParaPagina = (pagina) => {
    setPaginaAtual(pagina);
    loadData(pagina, busca, filtroTipo);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditar = (pedido) => {
    setEditando(pedido);
    // Formatar prev_entrega para o input date (YYYY-MM-DD)
    let prevEntrega = pedido.prev_entrega || '';
    if (prevEntrega && prevEntrega.includes('T')) {
      prevEntrega = prevEntrega.split('T')[0]; // Remove parte do tempo
    }
    setFormEdit({
      prev_entrega: prevEntrega,
      cod_rastreamento: pedido.cod_rastreamento || '',
      valor_frete: pedido.valor_frete || '',
      transportadora: pedido.transportadora || '',
      tipo_frete: pedido.tipo_frete || ''
    });
  };

  const handleCancelar = () => {
    setEditando(null);
    setFormEdit({});
  };

  const handleSalvar = async () => {
    if (!editando) return;
    
    // Validar campos obrigatórios
    if (formEdit.valor_frete && !formEdit.tipo_frete) {
      alert('⚠️ Campo obrigatório!\n\nQuando há valor de frete, o Tipo de Frete (CIF/FOB) deve ser informado.');
      return;
    }
    
    // Validar previsão de entrega (OBRIGATÓRIO)
    if (!formEdit.prev_entrega || formEdit.prev_entrega === '') {
      alert('⚠️ PREVISÃO DE ENTREGA OBRIGATÓRIA!\n\nDefina a data prevista de entrega antes de salvar.\n\nEssa data será usada para calcular prazos e acompanhar entregas.');
      return;
    }
    
    setSalvando(true);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.put(`${API}/pedidos/${editando.id}`, {
        prev_entrega: formEdit.prev_entrega || null,
        cod_rastreamento: formEdit.cod_rastreamento || null,
        valor_frete: formEdit.valor_frete ? String(formEdit.valor_frete) : null,
        transportadora: formEdit.transportadora || null,
        tipo_frete: formEdit.tipo_frete || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Recarregar dados
      await loadData(paginaAtual, busca, filtroTipo);
      setEditando(null);
      setFormEdit({});
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar alterações');
    } finally {
      setSalvando(false);
    }
  };

  const getCampoFaltanteLabel = (campo) => {
    const labels = {
      prev_entrega: { label: 'Previsão', cor: 'bg-yellow-100 text-yellow-800' },
      cod_rastreamento: { label: 'Rastreio', cor: 'bg-blue-100 text-blue-800' },
      valor_frete: { label: 'Frete', cor: 'bg-purple-100 text-purple-800' },
      transportadora: { label: 'Transp.', cor: 'bg-orange-100 text-orange-800' },
      tipo_frete: { label: 'CIF/FOB', cor: 'bg-red-100 text-red-800' }
    };
    return labels[campo] || { label: campo, cor: 'bg-gray-100 text-gray-800' };
  };

  const limparFiltros = () => {
    setBuscaInput('');
    setBusca('');
    setFiltroTipo('TODOS');
    setPaginaAtual(1);
    loadData(1, '', '');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              ✅ Validação de Pedidos
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Complete os dados obrigatórios dos pedidos
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData(paginaAtual, busca, filtroTipo)}
            disabled={loading}
            style={{ 
              borderColor: colors.primaza.primary,
              color: colors.primaza.secondary 
            }}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6 space-y-6">
        {/* Erro de carregamento */}
        {erro && (
          <Alert className="border-red-300 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700 flex items-center justify-between">
              <span>{erro}</span>
              <Button
                size="sm"
                variant="outline"
                className="ml-4 border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => loadData(paginaAtual, busca, filtroTipo)}
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Tentar novamente
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Estatísticas */}
        {estatisticas && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
            {/* Card Completos */}
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <CheckCircle size={16} />
                  Completos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {estatisticas.completos}
                </div>
                <div className="text-xs text-gray-500">
                  {estatisticas.percentual_completo}% do total
                </div>
              </CardContent>
            </Card>

            {/* Card Incompletos */}
            <Card className="border-l-4 border-l-red-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <AlertTriangle size={16} />
                  Incompletos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {estatisticas.incompletos}
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-red-500 h-2 rounded-full"
                      style={{ width: `${100 - estatisticas.percentual_completo}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card Sem Previsão */}
            <Card 
              className={`border-l-4 border-l-yellow-500 cursor-pointer transition-all hover:shadow-md ${filtroTipo === 'prev_entrega' ? 'ring-2 ring-yellow-500' : ''}`}
              onClick={() => handleFiltroChange('prev_entrega')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Calendar size={16} />
                  Sem Previsão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {estatisticas.sem_previsao}
                </div>
              </CardContent>
            </Card>

            {/* Card Sem Rastreamento */}
            <Card 
              className={`border-l-4 border-l-blue-500 cursor-pointer transition-all hover:shadow-md ${filtroTipo === 'cod_rastreamento' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => handleFiltroChange('cod_rastreamento')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Package size={16} />
                  Sem Rastreio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {estatisticas.sem_rastreamento}
                </div>
              </CardContent>
            </Card>

            {/* Card Sem Frete */}
            <Card 
              className={`border-l-4 border-l-purple-500 cursor-pointer transition-all hover:shadow-md ${filtroTipo === 'valor_frete' ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => handleFiltroChange('valor_frete')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <DollarSign size={16} />
                  Sem Frete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  {estatisticas.sem_frete}
                </div>
              </CardContent>
            </Card>

            {/* Card Sem Transportadora */}
            <Card 
              className={`border-l-4 border-l-orange-500 cursor-pointer transition-all hover:shadow-md ${filtroTipo === 'transportadora' ? 'ring-2 ring-orange-500' : ''}`}
              onClick={() => handleFiltroChange('transportadora')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Truck size={16} />
                  Sem Transp.
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {estatisticas.sem_transportadora || 0}
                </div>
              </CardContent>
            </Card>

            {/* Card Sem Tipo Frete (CIF/FOB) */}
            <Card 
              className={`border-l-4 border-l-red-500 cursor-pointer transition-all hover:shadow-md ${filtroTipo === 'tipo_frete' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => handleFiltroChange('tipo_frete')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <AlertTriangle size={16} />
                  Sem CIF/FOB
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {estatisticas.sem_tipo_frete || 0}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search size={20} style={{ color: colors.primaza.primary }} />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                placeholder="Buscar por pedido, cliente, vendedor..."
                value={buscaInput}
                onChange={(e) => setBuscaInput(e.target.value)}
                className="flex-1"
              />
              <select
                value={filtroTipo}
                onChange={(e) => handleFiltroChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md"
                style={{ borderColor: colors.primaza.primary }}
              >
                <option value="TODOS">📋 Todos os Campos</option>
                <option value="prev_entrega">📅 Sem Previsão</option>
                <option value="cod_rastreamento">📦 Sem Rastreamento</option>
                <option value="valor_frete">💰 Sem Valor do Frete</option>
                <option value="transportadora">🚚 Sem Transportadora</option>
                <option value="tipo_frete">🏷️ Sem CIF/FOB</option>
              </select>
              {(buscaInput || filtroTipo !== 'TODOS') && (
                <Button variant="outline" onClick={limparFiltros}>
                  Limpar
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Mostrando {pedidos.length} de {totalPedidos} pedidos incompletos
              {filtroTipo !== 'TODOS' && ` (filtrado por ${getCampoFaltanteLabel(filtroTipo).label})`}
            </p>
          </CardContent>
        </Card>

        {/* Lista de Pedidos */}
        <Card>
          <CardHeader>
            <CardTitle>Pedidos para Validar</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.primaza.primary }} />
              </div>
            ) : pedidos.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {filtroTipo !== 'TODOS' || buscaInput 
                  ? 'Nenhum pedido encontrado com os filtros aplicados'
                  : '🎉 Todos os pedidos estão completos!'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pedido</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campos Faltantes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Previsão</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rastreio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frete</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transportadora</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pedidos.map((pedido) => (
                      <tr key={pedido.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{pedido.pedido}</td>
                        <td className="px-4 py-3 text-sm">{pedido.nome_cliente || '-'}</td>
                        <td className="px-4 py-3 text-sm">{pedido.vendedor || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {pedido.campos_faltantes?.map(campo => {
                              const { label, cor } = getCampoFaltanteLabel(campo);
                              return (
                                <Badge key={campo} className={`${cor} text-xs`}>
                                  {label}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                        
                        {editando?.id === pedido.id ? (
                          <>
                            <td className="px-4 py-3">
                              <Input
                                type="date"
                                value={formEdit.prev_entrega}
                                onChange={(e) => setFormEdit({...formEdit, prev_entrega: e.target.value})}
                                className="w-36"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <Input
                                value={formEdit.cod_rastreamento}
                                onChange={(e) => setFormEdit({...formEdit, cod_rastreamento: e.target.value})}
                                placeholder="Código"
                                className="w-32"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <Input
                                type="number"
                                step="0.01"
                                value={formEdit.valor_frete}
                                onChange={(e) => setFormEdit({...formEdit, valor_frete: e.target.value})}
                                placeholder="R$"
                                className="w-24"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={formEdit.transportadora}
                                onChange={(e) => setFormEdit({...formEdit, transportadora: e.target.value})}
                                className="px-2 py-1 border border-gray-300 rounded text-sm w-40"
                              >
                                <option value="">Selecione...</option>
                                {transportadoras.filter(t => t.ativa).map(t => (
                                  <option key={t.id} value={t.nome}>{t.nome}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={formEdit.tipo_frete}
                                onChange={(e) => setFormEdit({...formEdit, tipo_frete: e.target.value})}
                                className={`px-2 py-1 border rounded text-sm font-semibold ${
                                  formEdit.tipo_frete === 'FOB' 
                                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                    : formEdit.tipo_frete === 'CIF'
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-orange-400 bg-orange-50 text-orange-700'
                                }`}
                              >
                                <option value="">Selecione...</option>
                                <option value="CIF">CIF - Primaza paga</option>
                                <option value="FOB">FOB - Cliente paga</option>
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 justify-center">
                                <Button
                                  size="sm"
                                  onClick={handleSalvar}
                                  disabled={salvando}
                                  style={{ backgroundColor: colors.primaza.primary }}
                                >
                                  {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelar}
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-sm">
                              {pedido.prev_entrega ? (
                                new Date(pedido.prev_entrega).toLocaleDateString('pt-BR')
                              ) : (
                                <span className="text-yellow-600">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {pedido.cod_rastreamento || <span className="text-blue-600">-</span>}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {pedido.valor_frete ? (
                                `R$ ${Number(pedido.valor_frete).toFixed(2)}`
                              ) : (
                                <span className="text-purple-600">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {pedido.transportadora || <span className="text-orange-600">-</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {pedido.tipo_frete ? (
                                <Badge className={pedido.tipo_frete === 'CIF' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-blue-100 text-blue-800 border-blue-300'}>
                                  {pedido.tipo_frete}
                                </Badge>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditar(pedido)}
                                style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                              >
                                <Edit size={14} className="mr-1" />
                                Editar
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página <strong>{paginaAtual}</strong> de <strong>{totalPaginas}</strong>
                  {' '}({totalPedidos} pedidos)
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => irParaPagina(1)}
                    disabled={paginaAtual === 1 || loading}
                    variant="outline"
                    size="sm"
                  >
                    Primeira
                  </Button>
                  <Button
                    onClick={() => irParaPagina(paginaAtual - 1)}
                    disabled={paginaAtual === 1 || loading}
                    variant="outline"
                    size="sm"
                  >
                    Anterior
                  </Button>
                  
                  {/* Números de página */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                      let pageNum;
                      if (totalPaginas <= 5) {
                        pageNum = i + 1;
                      } else if (paginaAtual <= 3) {
                        pageNum = i + 1;
                      } else if (paginaAtual >= totalPaginas - 2) {
                        pageNum = totalPaginas - 4 + i;
                      } else {
                        pageNum = paginaAtual - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => irParaPagina(pageNum)}
                          variant={paginaAtual === pageNum ? "default" : "outline"}
                          size="sm"
                          disabled={loading}
                          style={paginaAtual === pageNum ? { backgroundColor: colors.primaza.primary } : {}}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    onClick={() => irParaPagina(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas || loading}
                    variant="outline"
                    size="sm"
                  >
                    Próxima
                  </Button>
                  <Button
                    onClick={() => irParaPagina(totalPaginas)}
                    disabled={paginaAtual === totalPaginas || loading}
                    variant="outline"
                    size="sm"
                  >
                    Última
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Validacao;
